<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { computed, Ref, ref, watch } from 'vue';
import {
  ConfirmationDialog,
  ControlButtons,
  DefaultLoader,
  Details,
  Menu,
  ShareDetails,
  TwoColumnsLayout,
  useDialogFocus,
} from '@ilteducation/components';
import {
  CollectionIcon,
  useCollectionName,
  getDateFromCollectionByKey,
  useMyCollectionsStore,
  useContentCollection,
  useCollectionContents,
} from '@ilteducation/content-collections';
import CollectionContent from '@/components/collections/CollectionContent.vue';
import CollectionSharePanel from '@/components/collections/CollectionSharePanel.vue';
import BackButton from '@/components/base/BackButton.vue';
import { useDisplay } from 'vuetify';
import { useFetchUsers } from '@ilteducation/customer-mgmt';
import { useLocale } from '@/plugins/translation';
import useSnackbar from '@/composables/use-snackbar';
import { useActivityStream } from '@/composables/use-activity-stream';
import type { BasicCollection, ContentReference } from '@ilteducation/content-collections-types';
import { useVideos } from '@ilteducation/videos';

const { fetchUsersByIds } = useFetchUsers();
const { t, l } = useLocale();
const route = useRoute();
const router = useRouter();
const collectionId = computed(() => route.params.id as string);
const { collection, isLoading, isOwner, subscribe, unsubscribe, isSubscriber, isMember, error } =
  useContentCollection(collectionId);
const { contents } = useCollectionContents(collectionId, ref({}));
const { update, remove } = useMyCollectionsStore();
const { mdAndUp, smAndDown } = useDisplay();
const { collectionName } = useCollectionName();
const { snackbarInfo } = useSnackbar();
const { addVideos } = useVideos();
const { logDeleteCollection } = useActivityStream();

const creator = ref<string>('');
const confirmDeleteShelfCollection = ref<boolean>(false);
const isSettingsDialogActive = ref<boolean>(false);
const isFollowing = ref(false);

const hasCreator = computed(() => !!collection.value?.creator);
const creatorId = computed(() => collection.value?.creator?.userId);

const hasAccessToOptions = computed(() => isOwner.value);
const canSubscribe = computed(() => !isOwner.value && !isMember.value);

// Initialize following state from isSubscriber
watch(
  isSubscriber,
  (newValue) => {
    isFollowing.value = !!newValue;
  },
  { immediate: true },
);

const fetchCreator = async (id: string) => {
  const res = await fetchUsersByIds([id], 1);
  creator.value = res[0]?.name ?? '';
};

watch(
  [collectionId, creatorId],
  async () => {
    creator.value = '';
    if (hasCreator.value && creatorId.value) {
      await fetchCreator(creatorId.value);
      return;
    }
  },
  { immediate: true },
);

watch(error, () => {
  if (error.value.status === 403) {
    router.push({ name: 'forbidden' });
  }
});

const references = computed<ContentReference[]>(() => {
  if (collection.value && collection.value.contents && 'references' in collection.value.contents) {
    return collection.value.contents.references;
  }
  return [];
});
const videosCount = computed(() => references.value.filter((ref) => ref.type === 'Video').length);
const articlesCount = computed(() => references.value.filter((ref) => ref.type === 'Article').length);
const materialsCount = computed(() => references.value.filter((ref) => ref.type === 'Material').length);

const controls = computed<ControlButtons[]>(() =>
  [
    {
      title: t(l.components.label_sharing_options),
      eventName: 'handle-open-ShareDialog',
      icon: 'fa-regular fa-share-from-square',
      isHidden: !hasAccessToOptions.value,
    },
    {
      title: isFollowing.value
        ? t(l.content_collections.label_unfollow_collection)
        : t(l.content_collections.label_follow_collection),
      eventName: 'toggleSubscription',
      icon: isFollowing.value ? 'mdi-check-circle' : 'mdi-plus-circle-outline',
      iconColor: isFollowing.value ? 'primary' : 'currentColor',
      isLoading: isLoading.value,
      isHidden: !canSubscribe.value,
    },
  ].filter((button) => !button.isHidden),
);

const additionalControls = computed(() =>
  [
    {
      title: t(l.components.button_remove_collection),
      fontColor: 'red',
      eventName: 'deleteCollection',
      icon: 'mdi-delete',
      isHidden: !hasAccessToOptions.value,
    },
  ].filter((button) => !button.isHidden),
);

const details: Ref<Details[]> = computed(() => {
  const allDetails: Details[] = [
    {
      title: '',
      value: `${videosCount.value} ${t(l.videos.title)}`,
      icon: 'far fa-video',
      bgColor: 'secondary-container',
      isHidden: !videosCount.value,
    },
    {
      title: '',
      value: `${articlesCount.value} ${t(l.articles, articlesCount.value)}`,
      icon: 'far fa-newspaper',
      bgColor: 'secondary-container',
      isHidden: !articlesCount.value,
    },
    {
      title: '',
      value: `${materialsCount.value} ${t(l.tabs.materials)}`,
      icon: 'far fa-file-lines',
      bgColor: 'secondary-container',
      isHidden: !materialsCount.value,
    },
    {
      title: t(l.label_status),
      value: collection.value?.isPublic ? t(l.share.label_status_public) : t(l.share.label_status_private),
      icon: collection.value?.isPublic ? 'far fa-globe' : 'far fa-lock',
      bgColor: 'surface-container-high',
      isHidden: !collection.value,
    },
    {
      title: t(l.created_by),
      value: creator.value,
      icon: 'far fa-crown',
      bgColor: 'surface-container-high',
      isHidden: false,
    },
    {
      title: t(l.last_updated),
      value: collection.value
        ? getDateFromCollectionByKey(collection.value, 'lastUpdatedAt' as keyof BasicCollection)
        : '',
      icon: 'far fa-circle-check',
      bgColor: 'surface-container-high',
      isHidden: !collection.value,
    },
    {
      title: t(l.created),
      value: collection.value ? getDateFromCollectionByKey(collection.value, 'createdAt' as keyof BasicCollection) : '',
      icon: 'far fa-calendar-days',
      bgColor: 'surface-container-high',
      isHidden: !collection.value,
    },
  ];

  return allDetails.filter((detail) => !detail.isHidden);
});

watch(contents, (newContents) => {
  if (newContents.length > 0) {
    addVideos(newContents.filter((c) => c.type === 'VideoV1'));
  }
});

const updateCollectionName = (name: string) => {
  if (collection.value?.id && name !== collection.value?.name) {
    update(collection.value.id, { name });
  }
};

const updateCollectionDescription = (description: string) => {
  if (collection.value?.id && description !== collection.value?.description) {
    update(collection.value.id, { description });
  }
};

const handleConfirmDeleteCollection = async () => {
  if (!collection.value?.id) {
    return;
  }
  confirmDeleteShelfCollection.value = false;
  await remove(collection.value.id);
  logDeleteCollection(route.fullPath);
  snackbarInfo(t(l.components.message_collection_deleted));
  setTimeout(() => {
    router.back();
  }, 2000);
};
const handleOpenSettings = () => {
  if (smAndDown.value) {
    isSettingsDialogActive.value = true;
  }
};

const showShareDialog = ref(false);
const { rememberFocus } = useDialogFocus([showShareDialog]);

const handleOpenShareDialog = () => {
  rememberFocus();
  showShareDialog.value = true;
};

const handleCloseShareDialog = () => {
  showShareDialog.value = false;
};

const toggleSubscription = async () => {
  if (isFollowing.value) {
    await unsubscribe();
    isFollowing.value = false;
    snackbarInfo(t(l.content_collections.message_unfollowed));
  } else {
    await subscribe();
    isFollowing.value = true;
    snackbarInfo(t(l.content_collections.message_followed));
  }
};
</script>

<template>
  <v-container fluid class="bg-surface-container h-100">
    <BackButton :absolute="true" @back="router.push({ name: 'myPage' })" />
    <div v-if="!collection">
      <DefaultLoader />
    </div>

    <TwoColumnsLayout v-else :full-height="mdAndUp" class="pt-14">
      <template #left>
        <ShareDetails
          v-if="collection"
          :details="details"
          :name="collectionName(collection?.name)"
          :can-edit-name="isOwner"
          share-ctrl-icon-size="medium"
          :control-buttons="controls"
          :additional-controls="additionalControls"
          :description="collection.description"
          icon-bg="accent-original"
          @on-edit-name="updateCollectionName"
          @on-edit-description="updateCollectionDescription"
          @delete-collection="confirmDeleteShelfCollection = true"
          @handle-open-Settings="handleOpenSettings"
          @handle-open-ShareDialog="handleOpenShareDialog"
          @toggle-subscription="toggleSubscription"
        >
          <template #icon>
            <CollectionIcon
              v-if="collection?.icon ?? 'history'"
              class="text-on-primary"
              :size="48"
              :symbol="collection?.icon ?? 'history'"
            />
          </template>
          <template #control-button-settings>
            <Menu
              v-if="!isSettingsDialogActive && mdAndUp"
              :open-on-click="true"
              :close-on-content-click="false"
              :title="t(l.components.label_settings_menu)"
            >
            </Menu>
          </template>
        </ShareDetails>
      </template>
      <template #right>
        <CollectionContent :content-collection-id="collectionId" :show-share-dialog="false" />
      </template>
    </TwoColumnsLayout>

    <ConfirmationDialog
      v-model="confirmDeleteShelfCollection"
      :text="t(l.share.message_remove_from_owners_will_delete_collection)"
      :no-btn-label="t(l.buttons.no_cancel)"
      :yes-btn-label="t(l.buttons.yes_delete)"
      @dialog-confirmation="handleConfirmDeleteCollection"
      @close-dialog="confirmDeleteShelfCollection = false"
    />

    <v-dialog v-model="showShareDialog" max-width="800" scrollable aria-labelledby="collection_share_dialog_title">
      <v-card>
        <v-card-text>
          <div class="d-flex justify-space-between align-center pb-0">
            <span id="collection_share_dialog_title" class="font-weight-bold text-h6">{{
              t(l.content_collections.label_share_collection)
            }}</span>
            <v-btn
              icon="mdi-close"
              variant="text"
              :aria-label="t(l.button_close)"
              @click="handleCloseShareDialog"
            ></v-btn>
          </div>
          <CollectionSharePanel v-if="collection" :content-collection-id="collectionId" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped lang="scss"></style>
